"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/lib/auth-context";

const SEEN_KEY = "blackdoor_wallet_seen_v1";
const ZERO     = "0x0000000000000000000000000000000000000000";

export function WalletOnboardingModal() {
  const { user } = useAuth();
  const [show,       setShow]       = useState(false);
  const [walletAddr, setWalletAddr] = useState<string | null>(null);
  const [copied,     setCopied]     = useState(false);
  const [addrCopied, setAddrCopied] = useState(false);
  const [confirmed,  setConfirmed]  = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    if (typeof window !== "undefined" && localStorage.getItem(SEEN_KEY) === "1") return;

    fetch(`/api/wallet?userId=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(d => {
        if (d.address) {
          setWalletAddr(d.address);
          setShow(true);
        }
      })
      .catch(() => {});
  }, [user?.id]);

  function dismiss() {
    localStorage.setItem(SEEN_KEY, "1");
    setShow(false);
  }

  function copyAddr() {
    if (!walletAddr) return;
    navigator.clipboard.writeText(walletAddr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  function copyAddrShort() {
    if (!walletAddr) return;
    navigator.clipboard.writeText(walletAddr).then(() => {
      setAddrCopied(true);
      setTimeout(() => setAddrCopied(false), 2000);
    });
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="onboarding-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center sm:p-4 bg-black/80 backdrop-blur-md"
          onClick={dismiss}
        >
          <motion.div
            key="onboarding-modal"
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0,      opacity: 1 }}
            exit={{ y: "100%",    opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 36 }}
            onClick={e => e.stopPropagation()}
            className="relative w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#09090f] shadow-2xl flex flex-col max-h-[92svh] sm:max-h-[90vh]"
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 sm:hidden shrink-0">
              <div className="h-1 w-10 rounded-full bg-white/12" />
            </div>

            <button
              onClick={dismiss}
              className="absolute top-4 right-4 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-[#09090f] text-white/30 hover:text-white hover:border-white/25 transition-all text-xs"
            >
              ✕
            </button>

            <div className="overflow-y-auto overscroll-contain flex-1 pb-2">

              {/* Header */}
              <div className="px-6 pt-5 pb-5 border-b border-white/8 text-center space-y-2">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border-2 border-white/25 bg-white/6 text-white text-xl font-black">
                  ✓
                </div>
                <h2 className="text-xl font-black tracking-tight text-white mt-2">
                  Wallet Created
                </h2>
                <p className="text-sm text-white/40 leading-relaxed">
                  Your personal BlackDoor wallet is ready on Arbitrum One.
                  This is where you send funds to bet.
                </p>
              </div>

              <div className="px-5 py-5 space-y-4">

                {/* Wallet address */}
                <div className="space-y-1.5">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-white/25">
                    Your Wallet Address
                  </p>
                  <button
                    onClick={copyAddr}
                    className="w-full flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/4 px-4 py-3 hover:bg-white/6 hover:border-white/18 active:scale-[0.98] transition-all group"
                  >
                    <span className="font-mono text-xs text-white/55 truncate">
                      {walletAddr ?? "loading…"}
                    </span>
                    <span className={`shrink-0 text-xs font-semibold transition-colors ${
                      copied ? "text-white" : "text-white/25 group-hover:text-white/50"
                    }`}>
                      {copied ? "Copied" : "Copy"}
                    </span>
                  </button>
                </div>

                {/* Smart contract security */}
                <div className="rounded-2xl border border-white/8 bg-white/3 overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/6 flex items-center gap-2.5">
                    <div className="h-4 w-4 rounded-full border border-white/18 flex items-center justify-center shrink-0">
                      <div className="h-1.5 w-1.5 rounded-full bg-white/50" />
                    </div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-white/35">
                      Smart Contract Secured
                    </p>
                  </div>
                  <div className="px-4 py-4 space-y-3.5">
                    <p className="text-xs text-white/50 leading-relaxed">
                      Your bets are locked inside immutable smart contracts on Arbitrum One.
                      BlackDoor <span className="font-bold text-white/70">cannot withdraw your funds</span> —
                      only you can claim winnings at resolution.
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      {[
                        { label: "Chain",    value: "Arbitrum One" },
                        { label: "Token",    value: "USDC" },
                        { label: "Protocol", value: "FPMM" },
                        { label: "Fee",      value: "2%" },
                      ].map(r => (
                        <div key={r.label} className="rounded-lg border border-white/6 bg-white/2 px-3 py-2">
                          <p className="text-white/25 text-[10px] uppercase tracking-widest">{r.label}</p>
                          <p className="text-white/65 font-bold mt-0.5">{r.value}</p>
                        </div>
                      ))}
                    </div>
                    {walletAddr && walletAddr !== ZERO && (
                      <button
                        onClick={copyAddrShort}
                        className="w-full flex items-center justify-between gap-2 rounded-xl border border-white/8 bg-white/3 px-3 py-2.5 hover:border-white/16 hover:bg-white/5 transition-all group"
                      >
                        <div className="min-w-0">
                          <p className="text-[10px] text-white/25 uppercase tracking-widest mb-0.5">Your contract address</p>
                          <p className="font-mono text-[11px] text-white/40 truncate">
                            {walletAddr.slice(0, 10)}…{walletAddr.slice(-6)}
                          </p>
                        </div>
                        <span className={`shrink-0 text-[11px] font-bold transition-colors ${
                          addrCopied ? "text-white" : "text-white/20 group-hover:text-white/45"
                        }`}>
                          {addrCopied ? "Copied" : "Copy"}
                        </span>
                      </button>
                    )}
                    <a
                      href={walletAddr ? `https://arbiscan.io/address/${walletAddr}` : "https://arbiscan.io"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between w-full rounded-xl border border-white/8 bg-white/3 px-3 py-2.5 hover:border-white/16 hover:bg-white/5 transition-all group"
                    >
                      <span className="text-xs text-white/35 group-hover:text-white/55 transition-colors">
                        Verify on Arbiscan
                      </span>
                      <span className="text-[10px] text-white/20 group-hover:text-white/40 transition-colors">↗</span>
                    </a>
                  </div>
                </div>

                {/* Critical warning */}
                <div className="rounded-2xl border-2 border-white/15 bg-white/4 overflow-hidden">
                  <div className="bg-white/6 px-4 py-3 flex items-center gap-2">
                    <span className="text-white text-base font-black leading-none shrink-0">!</span>
                    <p className="text-white font-black text-xs uppercase tracking-wider">
                      Only Use Arbitrum One Network
                    </p>
                  </div>
                  <div className="px-4 py-4 space-y-2.5">
                    <p className="text-white/80 font-black text-lg leading-tight tracking-tight">
                      Wrong network = lost funds.
                    </p>
                    <p className="text-white/40 text-sm leading-relaxed">
                      Sending from Ethereum, Polygon, BNB Chain, Solana, or any other
                      network means your funds{" "}
                      <span className="font-black text-white/65">will be permanently lost</span>
                      {" "}with no recovery.
                    </p>
                    <p className="text-white/30 text-xs leading-relaxed border-t border-white/8 pt-2.5">
                      Your address looks identical on every chain — but BlackDoor
                      only reads Arbitrum One.
                    </p>
                  </div>
                </div>

                {/* How to fund */}
                <div className="space-y-1.5">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-white/25">
                    How to Fund
                  </p>
                  <div className="rounded-xl border border-white/8 bg-white/3 divide-y divide-white/5">
                    {[
                      {
                        n: "1",
                        title: "From an exchange",
                        body: <>Withdraw USDC on Binance, Coinbase, or OKX — select <span className="font-bold text-white/60">Arbitrum One</span> as the network.</>,
                      },
                      {
                        n: "2",
                        title: "Bridge from another chain",
                        body: <>Use <span className="font-bold text-white/55">bridge.arbitrum.io</span> to move funds to Arbitrum One first.</>,
                      },
                      {
                        n: "3",
                        title: "Already on Arbitrum?",
                        body: <>Send USDC directly to your address above. Balance updates in seconds.</>,
                      },
                      {
                        n: "!",
                        title: "Also need ETH for gas",
                        body: <>Send ~0.001 ETH on Arbitrum One to your wallet. Required for every transaction. Cost: {"<"}$0.01.</>,
                      },
                    ].map(row => (
                      <div key={row.n} className="flex items-start gap-3 px-4 py-3">
                        <span className="shrink-0 text-[10px] font-black text-white/25 bg-white/6 rounded-full h-5 w-5 flex items-center justify-center mt-0.5">
                          {row.n}
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-white/70">{row.title}</p>
                          <p className="text-[11px] text-white/35 leading-snug mt-0.5">{row.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Confirm */}
                <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-white/8 bg-white/3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={e => setConfirmed(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-white cursor-pointer"
                  />
                  <span className="text-xs text-white/40 leading-relaxed">
                    I understand — I will only send USDC on{" "}
                    <span className="font-bold text-white/65">Arbitrum One</span>, and
                    I also need a small amount of ETH for gas fees.
                  </span>
                </label>

                {/* CTA */}
                <button
                  onClick={dismiss}
                  disabled={!confirmed}
                  className={`w-full rounded-xl py-4 text-sm font-black tracking-wide transition-all active:scale-[0.98] ${
                    confirmed
                      ? "bg-white text-black hover:bg-white/90"
                      : "bg-white/6 text-white/20 cursor-not-allowed"
                  }`}
                >
                  {confirmed ? "I'm Ready — Open BlackDoor" : "Check the box above to continue"}
                </button>

                <button
                  onClick={dismiss}
                  className="w-full text-xs text-white/18 hover:text-white/40 transition-colors py-1 pb-3"
                >
                  Skip for now
                </button>

              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
